/**
 * The `Filed:` receipt (v1) an agent's create, move, archive, restore or
 * rename reports: the project its items are now in, and what they are. The
 * backend validates it before it reaches a conversation; this keeps only a
 * well-formed one.
 */
const OPS = ["created", "moved", "archived", "restored", "renamed"] as const;
const ITEM_TYPES = ["frame", "task", "project"] as const;

type FilingOp = (typeof OPS)[number];

export interface FilingItem {
  type: (typeof ITEM_TYPES)[number];
  id: string;
  title: string;
  /** Where a moved frame came from (op "moved" only). */
  from_project_id?: string;
}

export interface FilingReceipt {
  v: 1;
  op: FilingOp;
  project_id: string;
  project_name: string;
  new_project: boolean;
  /** The name before a rename (op "renamed" only). */
  previous_name?: string;
  items: FilingItem[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isText(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

function filingItem(value: unknown): FilingItem | null {
  if (!isRecord(value) || !isText(value.id) || typeof value.title !== "string")
    return null;
  const type = ITEM_TYPES.find((candidate) => candidate === value.type);
  if (!type) return null;
  return {
    type,
    id: value.id,
    title: value.title,
    ...(isText(value.from_project_id)
      ? { from_project_id: value.from_project_id }
      : {}),
  };
}

export function filingReceipt(value: unknown): FilingReceipt | undefined {
  if (
    !isRecord(value) ||
    value.v !== 1 ||
    !isText(value.project_id) ||
    typeof value.project_name !== "string" ||
    !Array.isArray(value.items)
  )
    return undefined;
  const op = OPS.find((candidate) => candidate === value.op);
  const items = value.items.map(filingItem);
  if (!op || items.length === 0 || items.some((item) => item === null))
    return undefined;
  return {
    v: 1,
    op,
    project_id: value.project_id,
    project_name: value.project_name,
    new_project: value.new_project === true,
    ...(typeof value.previous_name === "string"
      ? { previous_name: value.previous_name }
      : {}),
    items: items as FilingItem[],
  };
}
